import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, Broadcast } from "@phosphor-icons/react";

const ago = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SyncStatus({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => api.get("/sync/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/sync/run");
      if (data.ok === false) {
        toast.error(data.detail || "Sync failed");
        return;
      }
      const parts = [];
      if (data.new_items) parts.push(`${data.new_items} new`);
      if (data.auto_scheduled) parts.push(`${data.auto_scheduled} auto-scheduled`);
      if (data.reversals_released) parts.push(`${data.reversals_released} reversal${data.reversals_released === 1 ? "" : "s"} released`);
      toast.success(parts.length ? `Synced — ${parts.join(" · ")}` : "Synced — already up to date");
      load();
      if (data.new_items || data.auto_scheduled) onSynced?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const last = status?.state?.last_synced_at;
  const live = status?.webhook_configured;

  return (
    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-5 py-3 mb-5" data-testid="sync-status-bar">
      <div className="flex items-center gap-3 text-sm">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-lg ${live ? "bg-positive/10 text-positive" : "bg-navy/5 text-navy/65"}`}
          title={live ? "Xero webhook configured — new items arrive in near real time" : "Live webhook not configured — use Sync now or scheduled imports"}
          data-testid="sync-live-badge">
          <Broadcast size={13} weight="fill" /> {live ? "Live sync on" : "Live sync off"}
        </span>
        <span className="text-navy/70">Last synced <span className="font-medium text-navy" data-testid="sync-last">{ago(last)}</span></span>
      </div>
      <button onClick={sync} disabled={syncing} data-testid="sync-now-btn"
        className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-60">
        <ArrowsClockwise size={15} weight="bold" className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}
